import React, { Component } from 'react';
import { connect } from 'react-redux';
import { DragSource, DropTarget } from 'react-dnd';

import { Row, Col, Artwork, Theme, Tab, TabBar, Button, MagicIcon, SkubotSpinner } from '@commonsku/styles';
import Files from './Files';
import Select from './Select';

import { createUploadProductImagePopup, createUploadFilesPopup, closePopup } from '../actions/popup';
import { createAddItemImage, createDeleteItemImage, createUpdateItemImage, createFinalizeAddItemImage } from '../actions';
import { createFinalizeUploadFile } from '../actions/file';

import { getItemImagesByItemId, getProductImagesByItemId, getFullItem } from '../selectors';

import { getFolderOptions, getMockupImprintOptions, getProofOptions } from '../selectors/dropdowns';

import { BASE_ZINDEX } from '../popup-factory';
import { getImageSrc } from '../utils';
import DragItemTypes from '../DragItemTypes';
import Toggle from './Toggle';
import { withRouter } from './helpers';
import { ImageEditorPopup } from './image-editor/ImageEditorPopup';
import { createLoadFoldersList } from '../actions/folders';
import { folderSorter } from '../redux/folders';
import { BulkMockupGeneratorPopup } from './mockup-generator/BulkMockupGeneratorPopup';
import './mockup-generator/BulkMockupGeneratorPopup.css';
import { sendMockupFeedback, persistPendingFeedback, clearPendingFeedback, startBulkMockupGeneration, persistBulkJobItems, pollBulkMockupJob } from '../services/mockupGeneration';
import { getMockupGeneratorItemLocations, isPromoStandardItem } from './mockup-generator/decorationModel';
import { aiMockupAndRewriterEnabled } from '../utils';
import { getProduct } from '../selectors';
import { resolveParentContext, MockupStatus } from './mockup-generator/mockupUtils';
import { resumeMockupGeneration, getPendingFeedbackStatus } from './mockup-generator/mockupGenerationFlow';
import { MockupFeedbackBanner } from './mockup-generator/MockupFeedbackBanner';
import ReactTooltip from './helpers/ReactTooltip';
import { Span } from './helpers/Typography';
import { createLoadOrder } from '../actions/order';
import { isPODShop } from './shop/helpers/helpers';

const imageDragSource = {
  beginDrag(props) {
    return {
      id: props.item.item_image_id,
      originalIndex: props.findIndex(props.item.item_image_id)
    };
  },

  endDrag(props, monitor) {
    const { id: droppedId, originalIndex } = monitor.getItem();
    const didDrop = monitor.didDrop();
    if (!didDrop) {
      props.moveImage(droppedId, originalIndex);
    } else {
      props.dropImage(droppedId);
    }
  }
};

const imageDropTarget = {
  hover(props, monitor) {
    const { id: draggedId } = monitor.getItem();
    const overId = props.item.item_image_id;

    if (draggedId !== overId) {
      const overIndex = props.findIndex(overId);
      props.moveImage(draggedId, overIndex);
    }
  }
};

const DraggableImage = DropTarget(DragItemTypes.IMAGE, imageDropTarget, connect => ({
  connectDropTarget: connect.dropTarget()
}))(DragSource(DragItemTypes.IMAGE, imageDragSource, (connect, monitor) => ({
  connectDragSource: connect.dragSource(),
  isDragging: monitor.isDragging()
}))(({
  item,
  isDragging,
  connectDragSource,
  connectDropTarget,
  ...props
}) => connectDragSource(
  connectDropTarget(
    <div>
      <Artwork picture={getImageSrc(item.image, 'medium')} {...props} />
    </div>
  )
)));

class SelectItemImagesPopup extends Component {

  constructor(props) {
    super(props);

    this.state = {
      hover: false,
      item_images: props.item_images,
      selectedTab: 'item',
      folder_id: 'all',
      showEditorPopupImage: null,
      mockupGeneratorImage: null,
      mockupStatus: { type: MockupStatus.IDLE, message: null },
      mockupFeedback: null,
      mockupJobId: null,
    };

    this.onMouseEnter = this.onMouseEnter.bind(this);
    this.onMouseLeave = this.onMouseLeave.bind(this);
    this.findIndex = this.findIndex.bind(this);
    this.moveImage = this.moveImage.bind(this);
    this.dropImage = this.dropImage.bind(this);
    this.handleChangeFolder = this.handleChangeFolder.bind(this);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.item_images.length !== this.state.item_images.length) {
      this.setState({ item_images: nextProps.item_images });
    } else if (nextProps.item_images.filter((ii, index) => ii.item_image_id !== this.state.item_images[index].item_image_id).length > 0) {
      this.setState({ item_images: nextProps.item_images });
    }
  }

  componentDidMount() {
    this.props.onLoadFoldersList(this.props.job_id, this.props.client_id);
    this.restorePendingFeedback();
    this.resumeMockupIfNeeded();
  }

  restorePendingFeedback = () => {
    const pending = getPendingFeedbackStatus(this.props.item_id);
    if (pending) {
      this.setState({ mockupStatus: pending, mockupJobId: pending.jobId ?? null });
    }
  };

  resumeMockupIfNeeded = () => {
    resumeMockupGeneration(this.props.item_id, {
      onStatusChange: (mockupStatus) => {
        this.setState({ mockupStatus, mockupJobId: mockupStatus.jobId ?? this.state.mockupJobId });
      },
      onFileReady: (file) => {
        this.props.onFinalizeUploadFile(file);
        this.props.onAddProductImage(file);
      },
    });
  };

  canEditImage = () => {
    const { locked } = this.props;
    return !locked;
  };

  renderImageEditor = (image) => {
    if (this.canEditImage()) {
      this.setState({ showEditorPopupImage: image });
    }
  };

  onMouseEnter(item_image_id) {
    return e => this.setState({ hover: item_image_id });
  }

  onMouseLeave() {
    this.setState({ hover: false });
  }

  findIndex(item_image_id) {
    return this.state.item_images.map(ii => ii.item_image_id).indexOf(item_image_id);
  }

  moveImage(item_image_id, index) {
    this.setState((prevState, props) => {
      const item_images = prevState.item_images.slice();
      const origIndex = item_images.map(ii => ii.item_image_id).indexOf(item_image_id);
      item_images.splice(index, 0, item_images.splice(origIndex, 1)[0]);
      return {
        item_images
      };
    });
  }

  dropImage(item_image_id) {
    const index = this.findIndex(item_image_id);
    this.props.onUpdateItemImage(item_image_id, index);
  }

  handleChangeFolder(folder_id) {
    this.setState({ folder_id });
  }

  renderMockupGenerator = (itemImage) => {
    this.setState({ mockupGeneratorImage: itemImage });
  };

  handleMockupFeedback = (isPositive) => {
    this.setState({ mockupFeedback: isPositive });
    sendMockupFeedback(this.state.mockupJobId, isPositive, this.props.item_id);
    if (this.props.item_id) clearPendingFeedback(this.props.item_id);
  };

  /**
   * Handle the generation of mockups for the item images.
   * @param {import('./mockup-generator/BulkMockupGeneratorPopup').BulkMockupGeneratorConfig} config - The configuration for the mockup generation.
   * @returns {Promise<void>}
   */
  handleGenerateMockup = async (config) => {
    const { job_id, shop_id, item_id } = this.props;
    const { parent_id } = resolveParentContext({ job_id, shop_id, item_id });

    this.setState({
      mockupGeneratorImage: null,
      mockupStatus: { type: MockupStatus.PROCESSING, message: 'Configuring products...' },
      mockupFeedback: null,
    });

    try {
      const toImageIdMap = (items) => Object.fromEntries(
        items
          .map(item => [item.item_id, item.item_images?.[0]?.item_image_id])
          .filter(([, imageId]) => imageId)
      );

      const { bulk_job_id } = await startBulkMockupGeneration({
        ...config,
        readyItemsWithItemImageId: toImageIdMap(config.readyItems),
        decoratedItemsWithItemImageId: toImageIdMap(config.decoratedItems),
      });

      const allIds = [
        ...config.readyItems.map(i => i.item_id),
        ...config.decoratedItems.map(i => i.item_id),
      ];
      persistBulkJobItems(bulk_job_id, allIds);

      await pollBulkMockupJob({
        bulkJobId: bulk_job_id,
        parentId: parent_id,
        onStatusChange: (status, response) => {
          if (status === 'completed') {
            const itemData = response.item_data_by_item?.[item_id];
            if (itemData?.file) {
              this.props.onFinalizeUploadFile(itemData.file);
            }
            if (itemData?.item_image && itemData?.item_images?.length) {
              this.props.onFinalizeItemImages(itemData.item_image, itemData.item_images);
            }
            if (this.props.item.order_id) {
              this.props.onLoadOrder(this.props.item.order_id);
            }
            persistPendingFeedback(item_id, bulk_job_id);
            this.setState({
              mockupStatus: { type: MockupStatus.SUCCESS, message: null, jobId: bulk_job_id },
              mockupJobId: bulk_job_id,
            });
          } else if (status === 'failed') {
            this.setState({
              mockupStatus: {
                type: MockupStatus.ERROR,
                message: response.error || 'Mockup generation failed. Please try again.',
              },
            });
          }
        },
      });
    } catch (error) {
      this.setState({
        mockupStatus: {
          type: MockupStatus.ERROR,
          message: error.message || 'Failed to start mockup generation. Please try again.',
        },
      });
    }
  };

  renderTabs() {
    const { clientFolders, jobFolders, onCreateUploadFilesPopup, onCreateUploadProductImagePopup, locked, is_project, client_id } = this.props;
    const { selectedTab, folder_id } = this.state;

    let optionFolders = clientFolders;
    if (selectedTab === 'project') {
      optionFolders = jobFolders;
    }

    optionFolders.sort(folderSorter);

    let tabs = [
      {
        key: 'item',
        label: 'Item Files'
      }
    ];
    if (is_project) {
      tabs.push({
        key: 'project',
        label: 'Project Files'
      });
    }
    if (client_id) {
      tabs.push({
        key: 'client',
        label: 'Client Files'
      });
    }

    return (
      <Row>
        <Col xs={6}>
          <TabBar>
            {tabs.map((tab, index) => <Tab key={index}
              selected={tab.key === selectedTab}
              onClick={() => { this.setState({ selectedTab: tab.key, folder_id: 'all' }); }}>
              {tab.label}
            </Tab>)}
          </TabBar>
        </Col>
        <Col xs={4}>
          {selectedTab === 'client' || selectedTab === 'project' ?
            <Select
              options={optionFolders}
              value={folder_id}
              change={this.handleChangeFolder}
              style={{ width: '150px', float: 'right', 'zIndex': 1008, marginTop: '10px' }}
            />
            : null}
        </Col>
        <Col xs={2}>
          {!locked &&
            <Button style={{ float: 'right' }} type="secondary" onClick={(e) => is_project ? onCreateUploadFilesPopup(client_id) : onCreateUploadProductImagePopup()}>Upload</Button>
          }
        </Col>
      </Row>
    );
  }

  renderTabContent() {
    const { job_id, client_id, onAddProductImage, product_images, autoHideOnError } = this.props;
    const { selectedTab, folder_id, item_images } = this.state;

    const item_image_file_ids = item_images.map(ii => ii.file_id);
    const available_product_images = product_images.filter(i => !item_image_file_ids.includes(i.file_id));

    let tbd = false;
    if (selectedTab === 'project' || (selectedTab === 'client' && folder_id === 'all')) {
      tbd = true;
    }

    switch (selectedTab) {
      case 'project':
        return <Files type="project" job_id={job_id} folder_id={folder_id !== 'all' ? folder_id : null} onSelectFile={onAddProductImage} exclude_file_ids={item_image_file_ids} tbd={tbd} />;
      case 'client':
        return <Files type="client" client_id={client_id} folder_id={folder_id !== 'all' ? folder_id : null} onSelectFile={onAddProductImage} exclude_file_ids={item_image_file_ids} tbd={tbd} />;
      default:
        return <Files
          type="item" onSelectFile={onAddProductImage} files={available_product_images} tbd={tbd}
          autoHideOnError={(autoHideOnError || []).indexOf('item') > -1}
        />;
    }
  }

  render() {
    const {
      onDeleteItemImage,
      onClosePopup,
      onAddProductImage,
      item_id,
      account_id,
      account_type,
      job_id,
      shop_id,
      isPoDShop,
      index,
      locked = true,
      itemLocations,
      isPromoStandard,
      mockupEnabled,
    } = this.props;
    const { item_images, hover, showEditorPopupImage, mockupGeneratorImage, mockupStatus } = this.state;
    const isProcessing = mockupStatus.type === MockupStatus.PROCESSING;
    const { parent_id, parent_type } = resolveParentContext({ job_id, shop_id, item_id });

    const imageContainerStyle = {
      position: 'relative'
    };

    const loaderStyle = {
      position: 'relative',
      backgroundColor: "#E1F7FA",
      borderRadius: "5px",
      padding: "1rem",
      textAlign: "center",
      fontSize: "0.8rem",
      color: "#00A0B6",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      gap: "2rem"
    };

    return (<>
      {showEditorPopupImage && <ImageEditorPopup
        imageSource={getImageSrc(showEditorPopupImage, 'large')}
        onClose={() => this.setState({ showEditorPopupImage: null })}
        onSave={onAddProductImage}
        parent_id={item_id}
        parent_type="ITEM"
        logo_parent_id={!!job_id ? job_id : (!!shop_id ? shop_id : item_id)}
        logo_parent_type={!!job_id ? 'JOB' : (!!shop_id ? 'SHOP' : 'ITEM')}
        account_id={account_id}
        account_type={account_type}
        overlayZIndex={BASE_ZINDEX + index + 1}
        zIndex={BASE_ZINDEX + index + 2}
      />}
      {mockupGeneratorImage && <BulkMockupGeneratorPopup
        items={[
          {
            item_id: item_id,
            parent_type: this.props.item.parent_type,
            copied_from: this.props.item.copied_from,
            item_decorations: this.props.item.item_decorations,
            item_images: [
              mockupGeneratorImage,
              ...item_images.filter(ii => ii.item_image_id !== mockupGeneratorImage.item_image_id),
            ],
            item_locations: itemLocations,
          },
        ]}
        accountId={account_id}
        parentId={parent_id}
        parentType={parent_type}
        overlayZIndex={BASE_ZINDEX + index + 1}
        zIndex={BASE_ZINDEX + index + 2}
        onClose={() => this.setState({ mockupGeneratorImage: null })}
        onGenerate={this.handleGenerateMockup}
        proofOptions={this.props.proofOptions}
      />}
      <div className="reveal large" style={{ display: 'block', height: 'auto', zIndex: BASE_ZINDEX + index }} role="dialog">
        <div className="row">
          <div className="small-12 columns">
            <h3>{!locked && 'Select '}Files</h3>
            <a className="button" style={{ position: 'fixed', top: '1rem', right: '1rem' }} onClick={() => onClosePopup()}>Close</a>
          </div>
        </div>
        <div className="row full-width popup-content" style={{ height: 'calc(600px - 2rem)' }}>
          <Theme>
            <div className="row">
              {!locked && item_images.length > 1 && <p>Drag images to rearrange</p>}
              {isProcessing && <div className="small-6 medium-4 large-2 columns end" style={loaderStyle}>
                <SkubotSpinner skubot={false} size='medium' style={{ margin: "auto" }}/>
                <p style={{ marginBottom: '0 !important', fontSize: '1rem' }}>{mockupStatus.message}</p>
              </div>}
              {item_images.map((i, index) => {
                return <Toggle key={i.item_image_id} render={(setToggle) => {
                  return <div
                    className="small-6 medium-4 large-2 columns end" style={imageContainerStyle}
                    onMouseEnter={this.onMouseEnter(i.item_image_id)} onMouseLeave={this.onMouseLeave}
                  >
                    {
                      locked || item_images.length === 1
                        ? <Artwork
                          picture={getImageSrc(i.image, 'medium')} onError={() => {
                            setToggle(false);
                          }}
                          onEdit={this.canEditImage() ? () => this.renderImageEditor(i.image) : undefined}
                        />
                        : <DraggableImage
                          item={i} findIndex={this.findIndex} moveImage={this.moveImage} dropImage={this.dropImage}
                          onError={() => {
                            setToggle(false);
                          }}
                        />
                    }
                    {
                      !locked && hover === i.item_image_id && <>
                        {item_images.length > 1 && <>
                          <a
                            className="button" style={{ position: 'absolute', top: '0.5rem', left: '0.5rem' }}
                            onClick={() => onDeleteItemImage(i.item_image_id)}
                          >Remove</a>
                          {this.canEditImage() && <a
                            className="button" style={{ position: 'absolute', top: '0.5rem', right: '0.5rem' }}
                            onClick={() => this.renderImageEditor(i.image)}
                          >Edit</a>}
                        </>}
                        {!isPoDShop && isPromoStandard && mockupEnabled && <Button
                          size="small"
                          disabled={isProcessing}
                          style={{ position: 'absolute', top: '4rem', right: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                          onClick={() => this.renderMockupGenerator(i)}
                          data-tip="Add mockups and decorator information with AI"
                          data-for="mockup-generator-button-tooltip"
                        >
                          <MagicIcon size="small" color='white' />
                          AI Configure
                        </Button>}
                      </>
                    }
                  </div>;
                }} />;
              })}
            </div>
            <MockupFeedbackBanner
              status={mockupStatus}
              onFeedback={this.handleMockupFeedback}
              mockupFeedback={this.state.mockupFeedback}
            />
            {!locked &&
              <Row>
                <Col xs={12}>
                  <p>Click images below to add to item</p>
                </Col>
                <Col xs={12}>
                  {this.renderTabs()}
                </Col>
                <Col xs={12}>
                  {this.renderTabContent()}
                </Col>
              </Row>
            }
          </Theme>
        </div>
      </div>
      <ReactTooltip id="mockup-generator-button-tooltip"><div>
        <Span bold style={{ color: 'var(--color-green-90)' }}>
          Add mockups and decorator information with AI
        </Span>
      </div></ReactTooltip>
    </>);
  }
}

const mapStateToProps = (state, ownProps) => {
  const is_project = /\/project\//.test(ownProps.router.location.pathname);
  const is_shop = /\/shop\//.test(ownProps.router.location.pathname);
  let job_id = null;
  let shop_id = null;
  let client_id = ownProps.account_id;
  if (is_project) {
    job_id = Object.keys(state.entities.projects)[0];
  }
  if (is_shop) {
    shop_id = Object.keys(state.entities.shops)[0];
  }
  const shop = shop_id ? state.entities.shops?.[shop_id] : undefined;
  const isPoDShop = !!shop && isPODShop(shop);
  let clientFolders = [{ key: 'all', value: 'All Files' }].concat(getFolderOptions(state).filter(f => f.type === 'CLIENT'));
  let jobFolders = [{ key: 'all', value: 'All Files' }].concat(getFolderOptions(state).filter(f => f.type === 'JOB'));

  const item = ownProps.item_id ? getFullItem(state, { item_id: ownProps.item_id }) : undefined;
  const product = item?.parent_id ? getProduct(state, { product_id: item.parent_id }) : undefined;
  const is3rdPartyDecorator = item?.is_3rd_party_decorator === 1 ?? false;

  const mockupEnabled = aiMockupAndRewriterEnabled(state.identity);

  const psProductId = product?.ext_product_id ?? null;

  return {
    identity: state.identity,
    item,
    psProductId,
    item_images: getItemImagesByItemId(state, ownProps),
    product_images: getProductImagesByItemId(state, ownProps),
    is_project,
    is_shop,
    clientFolders,
    jobFolders,
    job_id,
    shop_id,
    isPoDShop,
    client_id,
    isPromoStandard: isPromoStandardItem(item),
    is3rdPartyDecorator,
    imprintOptions: getMockupImprintOptions(state),
    itemLocations: getMockupGeneratorItemLocations(state, item),
    mockupEnabled,
    proofOptions: getProofOptions(state),
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  onFinalizeUploadFile: file => dispatch(createFinalizeUploadFile(file)),
  onCreateUploadProductImagePopup: () => dispatch(createUploadProductImagePopup(ownProps.item_id, ownProps.account_id, ownProps.account_type, false)),
  onAddProductImage: file => dispatch(createAddItemImage(ownProps.item_id, file.file_id)),
  onFinalizeItemImages: (item_image, item_images) => dispatch(createFinalizeAddItemImage(item_image, item_images)),
  onDeleteItemImage: item_image_id => dispatch(createDeleteItemImage(item_image_id)),
  onUpdateItemImage: (item_image_id, index) => dispatch(createUpdateItemImage(item_image_id, index)),
  onClosePopup: () => dispatch(closePopup()),
  onCreateUploadFilesPopup: (client_id) => dispatch(createUploadFilesPopup(ownProps.item_id, 'ITEM', client_id, true, true)),
  onLoadFoldersList: (job_id, client_id) => {
    dispatch(createLoadFoldersList(job_id, 'JOB', client_id));
  },
  onLoadOrder: (order_id) => dispatch(createLoadOrder(order_id, true)),
  // Provide a stable dispatch function for persistDecorationUpdates helper
  dispatchPersistDecorationUpdates: (action) => dispatch(action),
});

const ConnectedSelectItemImagesPopup = withRouter(connect(mapStateToProps, mapDispatchToProps)(SelectItemImagesPopup));
export default ConnectedSelectItemImagesPopup;
